<template>
  <div class="p-6 w-[400px]">
    <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6">
      {{ isEditing ? 'Edit Webhook' : 'Add Webhook' }}
    </h3>

    <div class="space-y-4">
      <UiControl id="webhook-name" label="Name">
        <UiInput v-model="form.name" placeholder="e.g. GoHighLevel CRM" />
      </UiControl>

      <UiControl id="webhook-url" label="URL">
        <UiInput v-model="form.url" placeholder="https://example.com/webhook" />
      </UiControl>

      <UiControl id="webhook-enabled" label="Enabled">
        <div class="flex items-center gap-2">
          <button
            class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out"
            :class="form.enabled ? 'bg-primary-500' : 'bg-gray-300 dark:bg-gray-600'"
            @click="form.enabled = !form.enabled"
          >
            <span
              class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
              :class="form.enabled ? 'translate-x-5' : 'translate-x-0'"
            />
          </button>
          <span class="text-sm text-gray-600 dark:text-gray-300">
            {{ form.enabled ? 'Active' : 'Disabled' }}
          </span>
        </div>
      </UiControl>

      <!-- Secret token (read-only, shown after create) -->
      <div v-if="isEditing">
        <UiControl id="webhook-secret" label="Secret Token">
          <div class="flex items-center gap-2">
            <code class="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-gray-600 dark:text-gray-300 flex-1 truncate">
              {{ webhook.masked_secret_token || 'Not set' }}
            </code>
          </div>
          <p class="text-xs text-gray-400 mt-1">Used to sign webhook payloads (HMAC-SHA256). Token is auto-generated.</p>
        </UiControl>
      </div>

      <!-- Error message -->
      <div v-if="errorMessage" class="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-300">
        {{ errorMessage }}
      </div>
    </div>

    <!-- Actions -->
    <div class="flex items-center justify-end gap-3 mt-8">
      <button
        class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
        @click="$emit('on-close-panel')"
      >
        Cancel
      </button>
      <button
        class="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50"
        :disabled="submitting"
        @click="handleSubmit"
      >
        {{ submitting ? 'Saving...' : (isEditing ? 'Update' : 'Create') }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { createWebhook, updateWebhook } from '~/javascript/api/onboarding_webhooks'
import UiControl from '~/javascript/components/ui/form/control/control.vue'
import UiInput from '~/javascript/components/ui/form/input/input.vue'

const props = defineProps({
  webhook: { type: Object, default: null }
})

const emit = defineEmits(['on-saved', 'on-close-panel'])

const isEditing = computed(() => !!props.webhook)
const submitting = ref(false)
const errorMessage = ref('')

const form = ref({
  name: props.webhook?.name || '',
  url: props.webhook?.url || '',
  enabled: props.webhook?.enabled ?? true
})

async function handleSubmit() {
  errorMessage.value = ''
  submitting.value = true

  try {
    if (isEditing.value) {
      await updateWebhook(props.webhook.id, form.value)
    } else {
      await createWebhook(form.value)
    }
    emit('on-saved')
  } catch (e) {
    errorMessage.value = e.response?.data?.message || e.response?.data?.details?.name?.[0] || 'Failed to save webhook'
  } finally {
    submitting.value = false
  }
}
</script>
